// Angular
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';

// Angular Material
import { MatIconModule } from '@angular/material/icon';

// Third party
import { takeUntil } from 'rxjs';

// Local library
import { AuthFacade } from '@amwager/authentication/data-access';
import {
  AccountFacade,
  AccountService,
  ProfileSection,
  ProfileService
} from '@amwager/account/data-access';
import {
  CommonFacade,
  ENVIRONMENT,
  PermissionService,
  SessionService
} from '@amwager/data-access';
import {
  MatNotificationService,
  NumberWithCommasPipe,
  UnsubscribeObservableService,
  TranslatePipe
} from '@amwager/util';
import { GUEST, isOnline$ } from '@amwager/util-ts';
import { FundingComponent } from '@amwager/funding/feature-mat';
import { AccountSettingsComponent } from '../account-settings/account-settings.component';
import { AccountHistoryComponent } from '../account-history/account-history.component';
import { HelpComponent } from '../about-settings/help/help.component';
import { ResponsibleGamingComponent } from '../responsible-gaming/responsible-gaming.component';
import { PromotionsComponent } from '../promotions/promotions.component';

@Component({
  selector: 'feature-mat-profile',
  templateUrl: './profile.component.html',
  styleUrls: ['./profile.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    MatIconModule,
    NumberWithCommasPipe,
    AccountSettingsComponent,
    AccountHistoryComponent,
    FundingComponent,
    HelpComponent,
    ResponsibleGamingComponent,
    PromotionsComponent,
    TranslatePipe
  ]
})
export class ProfileComponent extends UnsubscribeObservableService implements OnInit, OnDestroy {
  private readonly HOST_COMPONENT = 'ProfileComponent';

  // Emits when the user closes the profile panel
  @Output() doClose = new EventEmitter<void>();

  userName = '';
  userIcon = '';
  balance = 0;
  promoCount = 0;
  isAuthenticated = false;
  isOnline = true;
  activeSection: ProfileSection = 'default';
  responsibleGaming = false;

  constructor(
    private readonly profileService: ProfileService,
    private readonly accountService: AccountService,
    private readonly sessionService: SessionService,
    private readonly permissionService: PermissionService,
    private readonly authFacade: AuthFacade,
    private readonly accountFacade: AccountFacade,
    private readonly commonFacade: CommonFacade,
    private readonly matNotificationService: MatNotificationService,
    private readonly router: Router,
    private readonly cdRef: ChangeDetectorRef,
    @Inject(ENVIRONMENT) readonly environment: string,
  ) {
    super();
  }

  /**
   * Init user info + subscribe to auth/balance/section/logout streams
   * @returns {void}
   */
  ngOnInit(): void {
    this.checkConnectivity();

    this.userName = this.profileService.getUserDisplayName(
      this.sessionService.getUserFName(),
      this.sessionService.getUserLast()
    );
    this.userIcon = this.profileService.getUserInitials(
      this.sessionService.getUserFName(),
      this.sessionService.getUserLast()
    );

    this.commonFacade.isAuthenticated$()
      .pipe(takeUntil(this.toUnsubscribe$))
      .subscribe((isAuth: boolean) => {
        this.isAuthenticated = isAuth;
        this.cdRef.markForCheck();
      });

    this.accountFacade.getBalance$()
      .pipe(takeUntil(this.toUnsubscribe$))
      .subscribe((balance?: number) => {
        this.balance = balance ?? 0;
        this.cdRef.markForCheck();
      });

    this.profileService.activeSection$
      .pipe(takeUntil(this.toUnsubscribe$))
      .subscribe((section: ProfileSection) => {
        this.activeSection = section;
        this.responsibleGaming = section === 'responsible-gaming';
        this.cdRef.markForCheck();
      });

    // Keep the sidebar promo badge in sync with the API list. The promotions
    // panel triggers the fetch; here we only read the resulting count.
    this.accountFacade.getPromoList$()
      .pipe(takeUntil(this.toUnsubscribe$))
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .subscribe((promoList: any) => {
        if (!promoList || promoList?.isSuccess === undefined) return;
        const sorted = this.accountService.sortByEndAndStart(promoList.response);
        this.promoCount = this.accountService.countAvailablePromoGiveaways(sorted);
        this.cdRef.markForCheck();
      });

    this.authFacade.isLogoutSuccess$()
      .pipe(takeUntil(this.toUnsubscribe$))
      .subscribe((isLogout?: boolean) => {
        if (!isLogout) return;
        this.promoCount = 0;
        this.matNotificationService.hide();
        this.close();
        this.cdRef.markForCheck();
      });
  }

  /**
   * Tear down parent unsubscribe stream on destroy
   * @returns {void}
   */
  override ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  /**
   * Select right-hand section — guards restricted sections behind login
   * @param {ProfileSection} section section to show
   * @returns {void}
   */
  selectSection(section: ProfileSection): void {

    const guardedSections: ProfileSection[] = [
      'funding',
      'my-profile',
      'account-settings',
      'account-history',
      'bet-results',
      'promo',
      'responsible-gaming',
      'help'
    ];

    if (guardedSections.includes(section) && !this.hasPermission()) {
      if (this.sessionService.getUserEmail() === GUEST || !this.sessionService.hasToken()) {
        this.profileService.closeProfile();
        this.router.navigate(['/login']);
      } else {
        this.showAlertInfo('You are not an Identified User.');
      }
      return;
    }

    this.profileService.setActiveSection(section);
  }

  /**
   * Open funding section
   * @returns {void}
   */
  openFunding(): void {
    this.selectSection('funding');
  }

  /**
   * Open my-profile section (placeholder alert for now)
   * @returns {void}
   */
  openProfile(): void {
    // this.showAlertInfo("We're working hard to bring this feature to you! Stay tuned for updates.");
  }

  /**
   * Open account settings section
   * @returns {void}
   */
  openAccountSettings(): void {
    this.selectSection('account-settings');
  }

  /**
   * Open account history section
   * @returns {void}
   */
  openAccountHistory(): void {
    this.selectSection('account-history');
  }

  /**
   * Open bet results section
   * @returns {void}
   */
  openBetResults(): void {
    // OLD CODE - opened the in-profile "Betting Results" placeholder panel
    // this.selectSection('bet-results');

    // NEW CODE - redirect the user to the Results page
    // Keep the same login/permission guarding used by selectSection() so
    // guests are routed to login and non-identified users get an alert.
    if (!this.hasPermission()) {
      if (this.sessionService.getUserEmail() === GUEST || !this.sessionService.hasToken()) {
        this.profileService.closeProfile();
        this.router.navigate(['/login']);
      } else {
        this.showAlertInfo('You are not an Identified User.');
      }
      return;
    }

    this.close();
    this.router.navigate(['/results']);
  }

  /**
   * Open promo section
   * @returns {void}
   */
  openPromo(): void {
    this.selectSection('promo');
  }

  /**
   * Toggle responsible gaming section
   * @returns {void}
   */
  openResponsibleGaming(): void {
    if (this.activeSection === 'responsible-gaming') {
      this.profileService.setActiveSection('default');
      return;
    }
    this.selectSection('responsible-gaming');
  }

  /**
   * Open help section
   * @returns {void}
   */
  openHelp(): void {
    this.selectSection('help');
  }

  /**
   * Confirm and dispatch logout via Material alert
   * @returns {void}
   */
  logout(): void {
    if (!this.isOnline) {
      this.commonFacade.setIsConnectedInternet(`${this.HOST_COMPONENT}:logout`, false);
      return;
    }

    this.matNotificationService
      .showAlert('Confirm', 'Are you sure you want to log out?', '', 'Yes', 'Cancel')
      .subscribe((confirmed: boolean) => {
        if (!confirmed) return;
        this.authFacade.logout();
      });
  }

  /**
   * Check if user role is allowed
   * @returns {boolean} true/false
   */
  hasPermission(): boolean {
    return this.permissionService.isAllowed();
  }

  /**
   * Show info alert
   * @param {string} message message to display
   * @returns {void}
   */
  showAlertInfo(message: string): void {
    this.matNotificationService.showAlert('Info', message, '', '', 'Close');
  }

  /**
   * Emit close event and close the profile panel
   * @returns {void}
   */
  close(): void {
    this.doClose.emit();
    this.profileService.closeProfile();
  }

  /**
   * Watch online/offline state and sync with common facade
   * @returns {void}
   */
  private checkConnectivity(): void {
    isOnline$(this.environment)
      .pipe(takeUntil(this.toUnsubscribe$))
      .subscribe((online: boolean) => {
        this.isOnline = online;
        this.commonFacade.setIsConnectedInternet(`${this.HOST_COMPONENT}:checkConnectivity`, online);
      });
  }
}
